import { useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { useIndustrySpecialist } from "./hooks/useIndustrySpecialist";
import useMutateIndustrySpecialist from "./hooks/useMutateIndustrySpecialist";
import { IndustrySpecialistFormData } from "./components/edit-industry-specialist-form/edit-industry-specialist.schema";
import BaseModal from "../shared/components/BaseModal";
import BaseButton from "components/common/BaseButton";
import { usAdminAppRoutes } from "route-definitions";
import ErrorPage from "../shared/components/BaseError";
import EditIndustrySpecialistForm from "./components/edit-industry-specialist-form/EditIndustrySpecialistForm";
import EditIndustrySpecialistSkeleton from "./components/skeletons/EditIndustryPageSkeleton";
import EntityPageLayout from "../shared/components/EntityPageLayout";

const EditIndustrySpecialistPage = () => {
  const intl = useIntl();
  const history = useHistory();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const { data, isLoading, isError, error, refetch } = useIndustrySpecialist({
    industrySpecialistId: entityId,
  });

  const {
    editIndustrySpecialist,
    editIndustrySpecialistLoading,
    editIndustrySpecialistError,
    editIndustrySpecialistSuccess,
  } = useMutateIndustrySpecialist({
    id: entityId,
    onEditSuccess: () => {
      setModalIsOpen(true);
    },
    onEditError() {
      setModalIsOpen(true);
    },
  });

  const onSubmit = (data: IndustrySpecialistFormData) => {
    editIndustrySpecialist({ ...data, industrySpecialistId: entityId });
  };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.editIndustrySpecialist">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.editIndustrySpecialist">
        <EditIndustrySpecialistSkeleton />
      </EntityPageLayout>
    );

  const handleCloseModal = () => {
    setModalIsOpen(false);
    if (editIndustrySpecialistSuccess) {
      history.push(usAdminAppRoutes.industrySpecialists);
    }
  };

  const industrySpecialistError = Array.isArray(
    editIndustrySpecialistError?.response?.data?.message
  )
    ? editIndustrySpecialistError.response.data.message[0]
    : editIndustrySpecialistError?.response?.data?.message;

  return (
    <EntityPageLayout headerMessage="usAdmin.editIndustrySpecialist">
      <BaseModal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        footer={
          <BaseButton
            handleSubmit={handleCloseModal}
            text={intl.formatMessage({ id: "usAdmin.ok" })}
          />
        }
      >
        {editIndustrySpecialistSuccess && (
          <h2>
            {intl.formatMessage({
              id: "usAdmin.industrySpecialistUpdatedSuccessfully",
            })}
          </h2>
        )}
        {editIndustrySpecialistError && <h2>{industrySpecialistError}</h2>}
      </BaseModal>
      <EditIndustrySpecialistForm
        industrySpecialist={data!}
        onSubmit={onSubmit}
        isSubmitting={editIndustrySpecialistLoading}
      />
    </EntityPageLayout>
  );
};

export default EditIndustrySpecialistPage;
